import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import TemplateUtils from "../classes/TemplateUtils";
import ListWrapper from "../utils/ListWrapper";
import TemplateCardView from "./TemplateCardView";
import ScrollView2 from "./ScrollView2";
import D3EAppTemplate from "../models/D3EAppTemplate";
import D3EAppTemplateCategory from "../models/D3EAppTemplateCategory";

type _TemplateListViewOnAddBuildTemplate = (template: D3EAppTemplate) => void;

type _TemplateListViewOnRemoveBuildTemplate = (
  template: D3EAppTemplate
) => void;

export interface TemplateListViewProps extends BaseUIProps {
  key?: string;
  categories: Array<D3EAppTemplateCategory>;
  appTemplateList: Array<D3EAppTemplate>;
  _categoriesHash?: number;
  _appTemplateListHash?: number;
  onAddBuildTemplate?: _TemplateListViewOnAddBuildTemplate;
  onRemoveBuildTemplate?: _TemplateListViewOnRemoveBuildTemplate;
}

class _TemplateListViewState extends ObservableComponent<TemplateListViewProps> {
  static defaultProps = {
    categories: [],
    appTemplateList: [],
    onAddBuildTemplate: null,
    onRemoveBuildTemplate: null,
  };
  wrapScrollController: ui.ScrollController = new ui.ScrollController();
  seletedTemplate: D3EAppTemplate = null;
  public constructor(props: TemplateListViewProps) {
    super(props);

    this.initState();
  }
  public get categories(): Array<D3EAppTemplateCategory> {
    return this.props.categories;
  }
  public get appTemplateList(): Array<D3EAppTemplate> {
    return this.props.appTemplateList;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.subscribeToList(this.categories, "categories");

    this.updateSyncCollProperty("categories", this.props.categories);

    this.subscribeToList(this.appTemplateList, "appTemplateList");

    this.updateSyncCollProperty("appTemplateList", this.props.appTemplateList);

    this.on(["appTemplateList", "categories", "categories.apps"], this.rebuild);
  }
  public componentDidUpdate(prevProps: TemplateListViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.categories !== this.props.categories) {
      this.updateObservableColl(
        "categories",
        prevProps.categories,
        this.props.categories
      );

      this.fire("categories", this);
    }

    if (prevProps.appTemplateList !== this.props.appTemplateList) {
      this.updateObservableColl(
        "appTemplateList",
        prevProps.appTemplateList,
        this.props.appTemplateList
      );

      this.fire("appTemplateList", this);
    }
  }
  public setSeletedTemplate(val: D3EAppTemplate): void {
    let isValChanged: boolean = this.seletedTemplate !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("seletedTemplate", this.seletedTemplate, val);

    this.seletedTemplate = val;

    this.fire("seletedTemplate", this);
  }
  public render(): ReactNode {
    return ScrollView2({
      child: ui.Wrap({
        spacing: 10,
        crossAxisAlignment: ui.WrapCrossAlignment.center,
        runSpacing: 10,
        children: [
          TemplateUtils.getAppTemplateList(this.categories)
            .toList()
            .expand((indexItem) => [
              TemplateCardView({
                obj: indexItem,
                appTemplateList: this.appTemplateList,
                onAddBuildTemplate: (template) => {
                  this.templateCardHandler(template, indexItem);
                },
                onRemoveBuildTemplate: (template) => {
                  this.removetemplateCardHandler(template, indexItem);
                },
                className: "hc vc",
                key: indexItem?.ident,
              }),
            ]),
        ],
        className: ui.join(this.props.className, "TemplateListView xa7f hc vc"),
        ...copyBaseUIProps(this.props),
      }),
      scrollDirection: ui.Axis.vertical,
      className: "TemplateListView xa7f hc vc",
      controller: this.wrapScrollController,
    });
  }
  public templateCardHandler = (
    template: D3EAppTemplate,
    indexItem: D3EAppTemplate
  ): void => {
    this.setSeletedTemplate(template);

    if (this.seletedTemplate !== null) {
      this.onAddBuildTemplate(this.seletedTemplate);
    }
  };
  public removetemplateCardHandler = (
    template: D3EAppTemplate,
    indexItem: D3EAppTemplate
  ): void => {
    this.setSeletedTemplate(template);

    if (this.seletedTemplate !== null) {
      this.onRemoveBuildTemplate(this.seletedTemplate);
    }
  };
  public get onAddBuildTemplate(): _TemplateListViewOnAddBuildTemplate {
    return this.props.onAddBuildTemplate;
  }
  public get onRemoveBuildTemplate(): _TemplateListViewOnRemoveBuildTemplate {
    return this.props.onRemoveBuildTemplate;
  }
}
export default function TemplateListView(props: TemplateListViewProps) {
  return React.createElement(
    _TemplateListViewState,
    { ..._TemplateListViewState.defaultProps, ...props },
    ListWrapper.fromInput<D3EAppTemplateCategory>(
      props.categories,
      "categories"
    ),
    ListWrapper.fromInput<D3EAppTemplate>(
      props.appTemplateList,
      "appTemplateList"
    )
  );
}
